import React, { useState, useEffect } from 'react';
import {
  fetchAccountSettings,
  toggleAccountActiveStatus,
  toggleCategoryActiveStatus,
  toggleAccountDeletedStatus,
  updateTimezone
} from 'services/accounts/accountService';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/common/Spinner';
import { ArrowLeft, ChevronDown, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTheme } from 'contexts/ThemeContext';

const TIMEZONE_OPTIONS: { label: string; offset: number }[] = [
  { label: '(UTC-12:00) Baker Island', offset: -720 },
  { label: '(UTC-11:00) Pago Pago', offset: -660 },
  { label: '(UTC-10:00) Honolulu', offset: -600 },
  { label: '(UTC-09:30) Marquesas Islands', offset: -570 },
  { label: '(UTC-09:00) Anchorage', offset: -540 },
  { label: '(UTC-08:00) Los Angeles', offset: -480 },
  { label: '(UTC-07:00) Denver', offset: -420 },
  { label: '(UTC-06:00) Chicago', offset: -360 },
  { label: '(UTC-05:00) New York', offset: -300 },
  { label: '(UTC-04:00) Halifax', offset: -240 },
  { label: '(UTC-03:30) St. John\'s', offset: -210 },
  { label: '(UTC-03:00) São Paulo', offset: -180 },
  { label: '(UTC-02:00) South Georgia', offset: -120 },
  { label: '(UTC-01:00) Azores', offset: -60 },
  { label: '(UTC+00:00) London, Dublin', offset: 0 },
  { label: '(UTC+01:00) Paris, Berlin', offset: 60 },
  { label: '(UTC+02:00) Cairo, Helsinki', offset: 120 },
  { label: '(UTC+03:00) Moscow, Nairobi', offset: 180 },
  { label: '(UTC+03:30) Tehran', offset: 210 },
  { label: '(UTC+04:00) Dubai, Baku', offset: 240 },
  { label: '(UTC+04:30) Kabul', offset: 270 },
  { label: '(UTC+05:00) Karachi, Tashkent', offset: 300 },
  { label: '(UTC+05:30) Mumbai, Colombo', offset: 330 },
  { label: '(UTC+05:45) Kathmandu', offset: 345 },
  { label: '(UTC+06:00) Dhaka, Almaty', offset: 360 },
  { label: '(UTC+06:30) Yangon', offset: 390 },
  { label: '(UTC+07:00) Bangkok, Jakarta', offset: 420 },
  { label: '(UTC+08:00) Singapore, Beijing', offset: 480 },
  { label: '(UTC+08:45) Eucla', offset: 525 },
  { label: '(UTC+09:00) Tokyo, Seoul', offset: 540 },
  { label: '(UTC+09:30) Adelaide', offset: 570 },
  { label: '(UTC+10:00) Sydney, Melbourne', offset: 600 },
  { label: '(UTC+10:30) Lord Howe Island', offset: 630 },
  { label: '(UTC+11:00) Solomon Islands', offset: 660 },
  { label: '(UTC+12:00) Auckland, Fiji', offset: 720 },
  { label: '(UTC+12:45) Chatham Islands', offset: 765 },
  { label: '(UTC+13:00) Samoa, Tonga', offset: 780 },
  { label: '(UTC+14:00) Line Islands', offset: 840 },
];

// Map connection_status from API to display label and style
const getConnectionStatusDisplay = (status: string, isDark: boolean) => {
  switch (status) {
    case 'active':
    case 'connected':
      return {
        label: 'Active',
        bg: isDark ? '#022C22' : '#F0FDF4',
        border: isDark ? '#009966' : '#86EFAC',
        color: isDark ? '#5EE9B5' : '#15803D',
      };
    case 'connecting':
      return {
        label: 'Connecting',
        bg: isDark ? '#1E293B' : '#EFF6FF',
        border: isDark ? '#334155' : '#93C5FD',
        color: isDark ? '#93C5FD' : '#1D4ED8',
      };
    case 'connection_issue':
    case 'error':
    case 'failed':
      return {
        label: 'Connection issue',
        bg: isDark ? '#2D1B1B' : '#FEF2F2',
        border: isDark ? '#7F1D1D' : '#FCA5A5',
        color: isDark ? '#FCA5A5' : '#DC2626',
      };
    case 'paused':
      return {
        label: 'Paused',
        bg: isDark ? '#1E293B' : '#FEF3C7',
        border: isDark ? '#334155' : '#FCD34D',
        color: isDark ? '#FCD34D' : '#92400E',
      };
    case 'disabled':
      return {
        label: 'Disabled',
        bg: isDark ? '#1E293B' : '#F1F5F9',
        border: isDark ? '#334155' : '#CBD5E1',
        color: isDark ? '#94A3B8' : '#64748B',
      };
    default:
      return {
        label: status || 'Unknown',
        bg: isDark ? '#1E293B' : '#F1F5F9',
        border: isDark ? '#334155' : '#CBD5E1',
        color: isDark ? '#94A3B8' : '#64748B',
      };
  }
};

// Capitalize first letter of category name for display
const formatCategoryName = (category: string) => {
  return category.charAt(0).toUpperCase() + category.slice(1);
};

const AccountSettingsTab: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string | undefined }>();
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any | null>(null);
  const [showDisableModal, setShowDisableModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [timezoneOffset, setTimezoneOffset] = useState<number | null>(null);
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const timezoneRef = React.useRef<HTMLDivElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const loadAccountSettings = async () => {
      try {
        setLoading(true);
        const response: any = await fetchAccountSettings(id);
        setAccountData(response);
        if (response?.timezone_offset !== undefined) {
          setTimezoneOffset(response.timezone_offset);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadAccountSettings();
    }
  }, [id]);

  // Close timezone dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (timezoneRef.current && !timezoneRef.current.contains(e.target as Node)) {
        setTimezoneOpen(false);
      }
    };
    if (timezoneOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [timezoneOpen]);

  const handleTimezoneChange = async (offset: number) => {
    if (!id) return;
    const prevOffset = timezoneOffset;
    setTimezoneOffset(offset);
    setTimezoneOpen(false);
    setTimezoneSaving(true);
    try {
      await updateTimezone(id, offset);
      toast.success('Timezone updated successfully');
    } catch {
      setTimezoneOffset(prevOffset);
    } finally {
      setTimezoneSaving(false);
    }
  };

  const handleToggleCategory = async (category: any) => {
    try {
      const newStatus = !category.is_active;
      const updatedCategories = accountData.categories.map((cat: any) =>
        cat.category === category.category
          ? { ...cat, is_active: newStatus }
          : cat
      );
      setAccountData({ ...accountData, categories: updatedCategories });
      await toggleCategoryActiveStatus(id, category.category, newStatus);
    } catch (err: any) {
      const originalCategories = accountData.categories.map((cat: any) =>
        cat.category === category.category
          ? { ...cat, is_active: category.is_active }
          : cat
      );
      setAccountData({ ...accountData, categories: originalCategories });
      setError(`Failed to update ${category.category} status: ${err.message}`);
    }
  };

  const handleToggleAccountStatus = async () => {
    try {
      const prevData = { ...accountData };
      const newStatus = !accountData.is_active;
      setAccountData({
        ...accountData,
        is_active: newStatus,
        connection_status: newStatus ? 'active' : 'disabled',
      });
      await toggleAccountActiveStatus(id, newStatus);
      setShowDisableModal(false);
    } catch (err: any) {
      setAccountData({ ...accountData, is_active: !accountData.is_active, connection_status: accountData.connection_status });
      setError(`Failed to update account status: ${err.message}`);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await toggleAccountDeletedStatus(id, true);
      setShowDeleteModal(false);
      navigate('/accounts');
    } catch (err: any) {
      setError(`Failed to delete account: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-full"
        style={{ background: isDark ? '#0F172B' : '#FFFFFF' }}
      >
        <Spinner size="large" text={t('accountSettings.messages.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen p-6"
        style={{ background: isDark ? '#0F172B' : '#FFFFFF' }}
      >
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="text-sm text-red-700">{t('accountSettings.errors.error')}: {error}</p>
        </div>
      </div>
    );
  }

  if (!accountData) {
    return (
      <div
        className="min-h-screen p-6"
        style={{ background: isDark ? '#0F172B' : '#FFFFFF' }}
      >
        <p style={{ color: isDark ? '#90A1B9' : '#62748E' }}>
          {t('accountSettings.messages.noData')}
        </p>
      </div>
    );
  }

  const isActive = accountData.is_active;
  const isDisabled = !isActive || accountData.is_deleted;
  const statusDisplay = getConnectionStatusDisplay(accountData.connection_status || (isActive ? 'active' : 'disabled'), isDark);
  const dividerColor = isDark ? '#1D293D' : '#E2E8F0';

  // Custom switch component
  const Switch = ({ checked, onClick, disabled = false }: { checked: boolean; onClick: () => void; disabled?: boolean }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex-shrink-0"
      style={{
        width: '40px',
        height: '24px',
        borderRadius: '12px',
        padding: '2px',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: checked
          ? (isDark ? '#51A2FF' : '#2B7FFF')
          : (isDark ? '#314158' : '#E2E8F0'),
        position: 'relative',
        transition: 'background 0.2s ease',
      }}
    >
      <div
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: checked
            ? (isDark ? '#0F172B' : '#FFFFFF')
            : '#FFFFFF',
          transform: checked ? 'translateX(16px)' : 'translateX(0)',
          transition: 'transform 0.2s ease',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      />
    </button>
  );

  return (
    <div
      className={`h-full overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
      style={{
        background: isDark ? '#0F172B' : '#FFFFFF',
        padding: '24px 32px',
        fontFamily: "'Geist', sans-serif",
      }}
    >
      {/* Header: Back button + Username + Status tag */}
      <div className="flex flex-row items-center gap-3 mb-8">
        <button
          onClick={() => navigate('/accounts')}
          className="flex items-center gap-2 transition-colors hover:opacity-80"
          style={{
            padding: '6px 12px',
            border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
            borderRadius: '8px',
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} color={isDark ? '#E2E8F0' : '#314158'} />
          <span
            style={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              color: isDark ? '#E2E8F0' : '#314158',
            }}
          >
            {t('accountSettings.labels.back') || 'Back'}
          </span>
        </button>

        <h1
          className="truncate"
          style={{
            fontWeight: 700,
            fontSize: '20px',
            lineHeight: '28px',
            color: isDark ? '#E2E8F0' : '#314158',
            margin: 0,
          }}
        >
          {accountData.username || t('accountSettings.labels.instagramAccount')}
        </h1>

        {/* Status tag from connection_status */}
        <span
          className="flex-shrink-0"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 10px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '18px',
            background: statusDisplay.bg,
            border: `1px solid ${statusDisplay.border}`,
            color: statusDisplay.color,
          }}
        >
          {statusDisplay.label}
        </span>
      </div>

      {/* Account Setting section */}
      <div style={{ marginBottom: '24px' }}>
        <h2
          style={{
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: isDark ? '#E2E8F0' : '#314158',
            margin: '0 0 12px 0',
          }}
        >
          {t('accountSettings.titles.accountSetting') || 'Account Setting'}
        </h2>

        {/* Divider */}
        <div style={{ height: '1px', background: dividerColor, marginBottom: '20px' }} />

        {/* Activate row */}
        <div
          className="flex items-center justify-between"
          style={{ marginBottom: '20px' }}
        >
          <div>
            <p
              style={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: isDark ? '#E2E8F0' : '#314158',
                margin: 0,
              }}
            >
              {t('accountSettings.labels.activate') || 'Activate'}
            </p>
            <p
              style={{
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: isDark ? '#90A1B9' : '#62748E',
                margin: 0,
              }}
            >
              {t('accountSettings.messages.activateDesc') || 'Enable or pause all automations for this account.'}
            </p>
          </div>

          <Switch
            checked={isActive}
            onClick={() => setShowDisableModal(true)}
            disabled={accountData.is_deleted}
          />
        </div>

        {/* Remove Account row */}
        <div className="flex items-center justify-between">
          <div>
            <p
              style={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: isDark ? '#E2E8F0' : '#314158',
                margin: 0,
              }}
            >
              {t('accountSettings.labels.removeAccount') || 'Remove Account'}
            </p>
            <p
              style={{
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: isDark ? '#90A1B9' : '#62748E',
                margin: 0,
              }}
            >
              {t('accountSettings.messages.removeDesc') || 'Permanently remove account'}
            </p>
          </div>

          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={accountData.is_deleted}
            style={{
              padding: '8px 16px',
              border: '1px solid #FF2056',
              borderRadius: '8px',
              background: 'transparent',
              color: '#FF2056',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              cursor: accountData.is_deleted ? 'not-allowed' : 'pointer',
              opacity: accountData.is_deleted ? 0.5 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {t('accountSettings.labels.removeAccount') || 'Remove Account'}
          </button>
        </div>

        {/* Timezone row */}
        <div
          className="flex items-center justify-between"
          style={{ marginTop: '20px' }}
        >
          <div>
            <p
              style={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: isDark ? '#E2E8F0' : '#314158',
                margin: 0,
              }}
            >
              Timezone
            </p>
            <p
              style={{
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: isDark ? '#90A1B9' : '#62748E',
                margin: 0,
              }}
            >
              Set the timezone for this account's automation schedule.
            </p>
          </div>

          <div ref={timezoneRef} style={{ position: 'relative', minWidth: '260px' }}>
            <button
              onClick={() => setTimezoneOpen(!timezoneOpen)}
              disabled={timezoneSaving}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                padding: '8px 12px',
                border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                borderRadius: '8px',
                background: isDark ? '#1E293B' : '#FFFFFF',
                color: isDark ? '#E2E8F0' : '#314158',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                cursor: timezoneSaving ? 'wait' : 'pointer',
                textAlign: 'left',
              }}
            >
              <span className="truncate">
                {TIMEZONE_OPTIONS.find(tz => tz.offset === timezoneOffset)?.label || 'Select timezone'}
              </span>
              <ChevronDown
                size={16}
                color={isDark ? '#90A1B9' : '#62748E'}
                style={{ flexShrink: 0, transform: timezoneOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              />
            </button>

            {timezoneOpen && (
              <div
                className={`${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: '4px',
                  width: '320px',
                  maxHeight: '280px',
                  overflowY: 'auto',
                  background: isDark ? '#1E293B' : '#FFFFFF',
                  border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                  borderRadius: '8px',
                  boxShadow: isDark
                    ? '0 4px 24px rgba(0,0,0,0.4)'
                    : '0 4px 24px rgba(0,0,0,0.1)',
                  zIndex: 50,
                  padding: '4px 0',
                }}
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <button
                    key={tz.offset}
                    onClick={() => handleTimezoneChange(tz.offset)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '8px 12px',
                      border: 'none',
                      background: tz.offset === timezoneOffset
                        ? (isDark ? '#0F172B' : '#F1F5F9')
                        : 'transparent',
                      color: isDark ? '#E2E8F0' : '#314158',
                      fontSize: '13px',
                      lineHeight: '20px',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (tz.offset !== timezoneOffset) {
                        e.currentTarget.style.background = isDark ? '#162032' : '#F8FAFC';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = tz.offset === timezoneOffset
                        ? (isDark ? '#0F172B' : '#F1F5F9')
                        : 'transparent';
                    }}
                  >
                    <span>{tz.label}</span>
                    {tz.offset === timezoneOffset && (
                      <Check size={16} color={isDark ? '#51A2FF' : '#2B7FFF'} style={{ flexShrink: 0 }} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Automation Setting section */}
      <div>
        <h2
          style={{
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: isDark ? '#E2E8F0' : '#314158',
            margin: '0 0 12px 0',
          }}
        >
          {isDisabled
            ? (t('accountSettings.titles.automationFeatures') || 'Automation features')
            : (t('accountSettings.titles.automationSetting') || 'Automation Setting')}
        </h2>

        {/* Divider */}
        <div style={{ height: '1px', background: dividerColor, marginBottom: '20px' }} />

        {/* Warning callout when disabled */}
        {isDisabled && (
          <div
            style={{
              background: isDark ? 'rgba(254, 243, 199, 0.1)' : '#FEF3C7',
              borderLeft: '4px solid #F59E0B',
              borderRadius: '4px',
              padding: '12px 16px',
              marginBottom: '20px',
            }}
          >
            <p
              style={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: isDark ? '#FCD34D' : '#92400E',
                margin: 0,
              }}
            >
              {t('accountSettings.messages.disabledWarning') || 'Features can only be toggled when the account is active'}
            </p>
          </div>
        )}

        {/* Category toggles */}
        <div className="flex flex-col" style={{ gap: '20px', opacity: isDisabled ? 0.5 : 1 }}>
          {accountData.categories?.map((category: any) => (
            <div
              key={category.category}
              className="flex items-center justify-between"
            >
              <div>
                <p
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: isDark ? '#E2E8F0' : '#314158',
                    margin: 0,
                  }}
                >
                  {formatCategoryName(category.category)}
                </p>
                <p
                  style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: isDark ? '#90A1B9' : '#62748E',
                    margin: 0,
                  }}
                >
                  {category.description || `${formatCategoryName(category.category)} based on your automation rules.`}
                </p>
              </div>

              <Switch
                checked={category.is_active}
                onClick={() => handleToggleCategory(category)}
                disabled={isDisabled}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onConfirm={handleToggleAccountStatus}
        title={accountData?.is_active ? t('accountSettings.modals.disableAccount') : t('accountSettings.modals.enableAccount')}
        message={
          accountData?.is_active
            ? t('accountSettings.modals.disableConfirmation')
            : t('accountSettings.modals.enableConfirmation')
        }
        confirmText={accountData?.is_active ? t('accountSettings.buttons.disable') : t('accountSettings.buttons.enable')}
        confirmColor="warning"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        title={t('accountSettings.modals.removeAccountPermanently')}
        message={t('accountSettings.modals.deleteConfirmation')}
        confirmText={t('accountSettings.modals.removeAccount')}
        confirmColor="danger"
      />
    </div>
  );
};

export default AccountSettingsTab;
